import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { Box, Button } from '@factorialco/deprecated-design-system'
import { BACKEND_DATE_FORMAT } from 'config/Vars'
import { EmployeesEmployeeSelectorInput } from 'generated/resources/datatable.graphql'
import { ExportTrainingsDataDocument, FullTrainingFragment } from 'generated/resources/graphql'
import moment from 'moment'

import DayPicker from 'modules/core/components/DayPicker'
import Loading from 'modules/core/components/Loading'
import Modal from 'modules/core/components/Modal'
import Select from 'modules/core/components/Select'
import TextField from 'modules/core/components/TextField'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { FormErrors, useForm } from 'modules/core/hooks/useForm'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { dateBackendToUI, dateUIToBackend } from 'modules/core/lib/dates'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { EmployeeSelectorV2 } from 'modules/employees/components/EmployeeSelectorV2'
import { useTrainingsExport } from 'modules/trainings/hooks/Exports/useTrainingsExport'

type Format = 'excel' | 'csv'

const FORMATS: Record<string, Format> = {
  excel: 'excel',
  csv: 'csv',
}

interface ConnectivityLogsExportFormValues {
  ids: string[]
  teamIds: string[]
  employeeSelection: EmployeesEmployeeSelectorInput
  categoryIds: string[]
  exportType: string
  format: string
  filename: string
  status: string[]
  from: string
  to: string
  scheduleAccessId: string
  companyId: string
}

interface ConnectivityLogsExportDialogProps {
  trainingId: string
  onClose: () => void
}
function ConnectivityLogsExportDialog({ trainingId, onClose }: ConnectivityLogsExportDialogProps) {
  const now = moment()
  const startDate = now.startOf('year').format(BACKEND_DATE_FORMAT)
  const endDate = now.endOf('year').format(BACKEND_DATE_FORMAT)
  const { navigation } = useNavigation()
  const navigate = useNavigate()
  const navigateToTrainings = () => {
    navigate(navigation.trainings.list.index.fullPath())
  }
  const { exportTrainings } = useTrainingsExport()
  const { company } = useCurrentSession()
  const { data, loading } = useQuery(ExportTrainingsDataDocument, {
    variables: { trainingIdAvailable: !!trainingId, trainingId },
  })
  const {
    access: { id: userId },
  } = useCurrentSession()

  const [selected, setSelectedEmployees] = useState<EmployeesEmployeeSelectorInput>({
    type: 'manual',
    ids: [],
  })
  const training: FullTrainingFragment | undefined = data?.trainings.trainings.find(
    (t) => t.id === trainingId
  )

  const form = useForm<ConnectivityLogsExportFormValues>({
    defaultValues: {
      filename: i18n.t('trainings.export_trainings.fields.default_name', {
        date: moment().format('YYYY/MM/DD'),
      }),
      ids: training?.id ? [training.id] : [],
      employeeSelection: {
        type: 'manual',
        ids: [],
      },
      scheduleAccessId: userId,
      companyId: company.id,
      format: 'excel',
      teamIds: [],
      categoryIds: [],
      exportType: 'connectivityLog',
      status: [],
      from: startDate,
      to: endDate,
    },
    validate: (data) => {
      const errors: FormErrors<ConnectivityLogsExportFormValues> = {}

      if (!data.filename || data.filename === '') {
        errors.filename = i18n.t('common.empty')
      }

      return errors
    },
    onSubmit: async (data) => {
      const result = await exportTrainings({
        ...data,
        employeeSelection: selected,
        exportType: 'connectivityLog',
        from: data.from,
        to: data.to,
      })

      const handled = handleResult(result, () => {
        app.setFlash({
          type: 'wrong',
          message: i18n.t('trainings.export_trainings.error'),
        })
      })

      if (handled.success) {
        app.setFlash({
          type: 'correct',
          message: i18n.t('trainings.export_trainings.success'),
        })

        navigateToTrainings()
      }

      return handled
    },
  })
  const employeeIds =
    data?.trainings?.trainingMemberships
      ?.map((m) => m?.access?.employee?.id)
      .filter((id): id is string => id !== undefined) ?? []

  // Update form ids when training loads or changes
  useEffect(() => {
    if (training?.id) {
      form.setValues({ ...form.values, ids: [training.id] })
    } else if (!loading) {
      // If training not found and not loading, reset to empty
      form.setValues({ ...form.values, ids: [] })
    }
  }, [training?.id, loading])

  if (!training && !loading) {
    return null
  }

  return (
    <Modal
      title={i18n.t('trainings.export_trainings.connectivity_logs_title', {
        trainingName: training?.name ?? '',
      })}
      description={i18n.t('trainings.export_trainings.description')}
      onClose={navigateToTrainings}
      onSubmit={() => {
        form.handleSubmit()
        onClose()
      }}
      footer={
        <Box flexDirection="row" gap="s8">
          <Button.Router to={navigation.trainings.list.index.fullPath()} type="secondary">
            {i18n.t('trainings.export_trainings.cancel')}
          </Button.Router>
          <Button submit>{i18n.t('trainings.export_trainings.export')}</Button>
        </Box>
      }
    >
      {loading ? (
        <Loading />
      ) : (
        <Box gap="s16">
          <TextField
            defaultValue={form.values.filename}
            label={i18n.t('trainings.export_trainings.fields.name')}
            onChange={(value) => form.setValues({ ...form.values, filename: value })}
            error={form.errors.filename}
          />
          <Select
            value={form.values.format}
            label={i18n.t('trainings.export_trainings.fields.format')}
            options={[
              {
                label: i18n.t('trainings.export_trainings.fields.formats.excel'),
                value: FORMATS.excel,
              },
              {
                label: i18n.t('trainings.export_trainings.fields.formats.csv'),
                value: FORMATS.csv,
              },
            ]}
            onChange={(value) => form.setValues({ ...form.values, format: value })}
          />
          <EmployeeSelectorV2
            forcedFilters={{
              employeeIds: employeeIds,
            }}
            multiple
            onChange={setSelectedEmployees}
            filters={['jobRole', 'team', 'workplace', 'legalEntity']}
          />
          <Box flexDirection="row" gap="s16">
            <DayPicker
              mode="single"
              label={i18n.t('trainings.export_trainings.fields.from')}
              onChange={(date) => {
                form.setValues({
                  ...form.values,
                  from: dateUIToBackend(date),
                })
              }}
              value={dateBackendToUI(form.values.from)}
            />
            <DayPicker
              mode="single"
              label={i18n.t('trainings.export_trainings.fields.to')}
              onChange={(date) => form.setValues({ ...form.values, to: dateUIToBackend(date) })}
              value={dateBackendToUI(form.values.to)}
            />
          </Box>
        </Box>
      )}
    </Modal>
  )
}

export default ConnectivityLogsExportDialog
